/* eslint-disable react-hooks/exhaustive-deps */
import { isValidElement, useEffect, useRef } from 'react';
import IntroJS from 'intro.js';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';

import * as introJsPropTypes from './helpers/proptypes';
import * as introJsDefaultProps from './helpers/defaultProps';
import { scenariosSteps } from './scenarios';
import 'intro.js/introjs.css';

function Tutorial(props) {
  const {
    onExit,
    onBeforeExit,
    onBeforeChange,
    onPreventChange,
    onAfterChange,
    enabled,
    initialStep,
    onStart,
    onChange,
    onComplete,
    options,
    scenario,
  } = props;

  let introJs;
  let steps;

  const manualStateRef = useRef({
    isConfigured: false,
    // We need to manually keep track of the visibility state to avoid a callback hell
    isVisible: false,
  });

  useEffect(() => {
    introJs = null;
  }, []);

  function conditionWatcher({ target }) {
    const curStep = steps[introJs._currentStep];

    if (curStep.passCondition(target) === true) {
      const transitionDelay = get(
        steps[introJs._currentStep],
        'transitionDelay',
        0
      );

      setTimeout(() => {
        introJs.goToStepNumber(introJs._currentStep + 2);
      }, transitionDelay);
    }
  }

  /**
   * Updates the element associated to a step based on its index.
   * This is useful when the associated element is not present in the DOM on page load.
   * @param {number} stepIndex - The index of the step to update
   */
  function updateStepElement(stepIndex) {
    const element = document.querySelector(
      introJs._options.steps[stepIndex].element
    );

    if (element) {
      introJs._introItems[stepIndex].element = element;
      introJs._introItems[stepIndex].position =
        introJs._options.steps[stepIndex].position || 'auto';
    }
  }

  /**
   * Triggered when tutorial is exited
   */
  function onExitHandler() {
    manualStateRef.current.isVisible = false;
    manualStateRef.current.isConfigured = false;

    onExit(introJs._currentStep);

    steps.forEach((step) => {
      const passTarget = document.querySelector(step.passTarget);

      if (passTarget) {
        passTarget.removeEventListener(
          step.passEventType,
          conditionWatcher,
          false
        );
      }
    });
  }

  /**
   * Triggered before exiting the intro
   * @return {Boolean} Returning `false` will prevent exiting the intro
   */
  function onBeforeExitHandler() {
    if (onBeforeExit) {
      return onBeforeExit(introJs._currentStep);
    }

    return true;
  }

  /**
   * Triggered before changing step
   * @return {Boolean} Returning `false` will prevent the step transition
   */
  function onBeforeChangeHandler() {
    if (!manualStateRef.current.isVisible) {
      return true;
    }

    updateStepElement(introJs._currentStep);

    const curStep = steps[introJs._currentStep];
    const passTarget = document.querySelector(curStep.passTarget);

    if (passTarget) {
      passTarget.addEventListener(
        curStep.passEventType,
        conditionWatcher,
        false
      );
    }

    if (onBeforeChange) {
      const continueStep = onBeforeChange(introJs._currentStep);

      if (continueStep === false && onPreventChange) {
        setTimeout(() => {
          onPreventChange(introJs._currentStep);
        }, 0);
      }

      return continueStep;
    }

    return true;
  }

  /**
   * Triggered after changing step
   * @param {HTMLElement} element - The element associated to the new step
   */
  function onAfterChangeHandler(element) {
    if (!manualStateRef.current.isVisible) {
      return;
    }

    if (onAfterChange) {
      onAfterChange(introJs._currentStep, element, steps[introJs._currentStep]);
    }
  }

  /**
   * Triggered when changing step
   * @param {HTMLElement} element - The element associated to the next step
   */
  function onChangeHandler(element) {
    if (!manualStateRef.current.isVisible) {
      return;
    }

    if (onChange) {
      onChange(introJs._currentStep, element, steps[introJs._currentStep]);
    }
  }

  /**
   * Triggered when completing all the steps
   */
  function onCompleteHandler() {
    if (onComplete) {
      onComplete();
    }
  }

  /**
   * Installs Intro.js
   */
  function installIntroJs() {
    introJs = IntroJS();

    introJs.onexit(onExitHandler);
    introJs.onbeforeexit(onBeforeExitHandler);
    introJs.onbeforechange(onBeforeChangeHandler);
    introJs.onafterchange(onAfterChangeHandler);
    introJs.onchange(onChangeHandler);
    introJs.oncomplete(onCompleteHandler);
  }

  /**
   * Configures Intro.js, if not already configured
   */
  function configureIntroJs() {
    const sanitizedSteps = steps.map((step) => {
      if (isValidElement(step.intro)) {
        return {
          ...step,
          intro: renderToStaticMarkup(step.intro),
        };
      }

      return step;
    });

    introJs.setOptions({ ...options, steps: sanitizedSteps });
    manualStateRef.current.isConfigured = true;
  }

  /**
   * Renders the Intro.js steps
   */
  function renderSteps() {
    if (enabled && steps.length > 0 && !manualStateRef.current.isVisible) {
      introJs.start();

      manualStateRef.current.isVisible = true;

      introJs.goToStepNumber(initialStep);

      if (onStart) {
        onStart(introJs._currentStep);
      }
    } else if (!enabled && manualStateRef.current.isVisible) {
      manualStateRef.current.isVisible = false;

      introJs.exit();
    }
  }

  useEffect(() => {
    steps = scenariosSteps[scenario - 1];
    installIntroJs();

    if (enabled && !manualStateRef.current.isConfigured) {
      configureIntroJs();
      renderSteps();
    }
  }, [props]);

  return null;
}

Tutorial.propTypes = {
  enabled: PropTypes.bool,
  initialStep: PropTypes.number.isRequired,
  scenario: PropTypes.number.isRequired,
  onStart: PropTypes.func,
  onExit: PropTypes.func.isRequired,
  onBeforeExit: PropTypes.func,
  onBeforeChange: PropTypes.func,
  onAfterChange: PropTypes.func,
  onChange: PropTypes.func,
  onPreventChange: PropTypes.func,
  onComplete: PropTypes.func,
  options: introJsPropTypes.options,
};

Tutorial.defaultProps = {
  enabled: false,
  onStart: null,
  onBeforeExit: null,
  onBeforeChange: null,
  onAfterChange: null,
  onChange: null,
  onPreventChange: null,
  onComplete: null,
  options: introJsDefaultProps.options,
};

export default Tutorial;
